
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { supabase, adminOperation } from '../../utils/supabase';
import type { Shelter, ShelterEvent } from '../../utils/supabase';

export default function ShelterEvents() {
  const { id } = useParams();
  const [shelter, setShelter] = useState<Shelter | null>(null);
  const [events, setEvents] = useState<ShelterEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAttendModal, setShowAttendModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ShelterEvent | null>(null);
  const [, setCurrentImageIndex] = useState(0);
  const [currentEventImageIndex, setCurrentEventImageIndex] = useState(0);
  const [attendeeForm, setAttendeeForm] = useState({
    name: '',
    phone: '',
    email: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (id) {
      loadShelterAndEvents();
    }
  }, [id]);

  const loadShelterAndEvents = async () => {
    try {
      // Cargar refugio
      const { data: shelterData } = await supabase
        .from('shelters')
        .select('*')
        .eq('id', id)
        .single();

      if (shelterData) {
        setShelter(shelterData);

        // Cargar eventos del refugio
        const { data: eventsData } = await supabase
          .from('shelter_events')
          .select('*')
          .eq('shelter_id', id)
          .eq('active', true)
          .order('event_date', { ascending: true });

        setEvents(eventsData || []);
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleAttendEvent = () => {
    setShowEventModal(false);
    setShowAttendModal(true);
  };

  const submitAttendance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEvent) return;

    setSubmitting(true);

    try {
      // Registrar asistencia
      const { error: attendeeError } = await supabase
        .from('event_attendees')
        .insert([{
          event_id: selectedEvent.id,
          attendee_name: attendeeForm.name,
          attendee_phone: attendeeForm.phone || null,
          attendee_email: attendeeForm.email || null
        }]);

      if (attendeeError) throw attendeeError;

      // Actualizar contador de asistentes
      const newCount = selectedEvent.attendees_count + 1;
      const { error: updateError } = await adminOperation('update', 'shelter_events', {
        attendees_count: newCount
      }, selectedEvent.id);

      if (updateError) throw updateError;

      // Actualizar estado local
      setEvents(prev => prev.map(event => 
        event.id === selectedEvent.id 
          ? { ...event, attendees_count: newCount }
          : event
      ));

      // Actualizar el evento seleccionado también
      setSelectedEvent(prev => prev ? { ...prev, attendees_count: newCount } : null);

      setShowAttendModal(false);
      setAttendeeForm({ name: '', phone: '', email: '' });
      alert('¡Gracias por confirmar tu asistencia! Te esperamos en el evento.');

    } catch (error) {
      console.error('Error registrando asistencia:', error);
      alert('Error al registrar tu asistencia. Por favor intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeModals = () => {
    setShowEventModal(false);
    setShowAttendModal(false);
    setSelectedEvent(null);
    setCurrentImageIndex(0);
    setCurrentEventImageIndex(0);
    setAttendeeForm({ name: '', phone: '', email: '' });
  };

  // Generar array de imágenes del evento seleccionado
  const eventImages = selectedEvent ? [
    selectedEvent.image_url_1,
    selectedEvent.image_url_2,
    selectedEvent.image_url_3,
    selectedEvent.image_url_4,
    selectedEvent.image_url_5
  ].filter(Boolean) : [];

  // Si no hay imágenes del evento, usar imagen por defecto
  const displayEventImages = eventImages.length > 0 ? eventImages : [
    'https://readdy.ai/api/search-image?query=Animal%20shelter%20community%20event%20with%20volunteers%20and%20rescued%20dogs%2C%20outdoor%20gathering%20with%20people%20helping%20animals%2C%20warm%20community%20atmosphere%20with%20banners%20and%20activities%20for%20pet%20adoption%20awareness&width=800&height=600&seq=event-modal-1&orientation=landscape'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando eventos...</p>
        </div>
      </div>
    );
  }

  if (!shelter) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-error-warning-line text-6xl text-gray-400 mb-4"></i>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Refugio no encontrado</h2>
          <p className="text-gray-600 mb-6">El refugio que buscas no existe o ha sido eliminado.</p>
          <button 
            onClick={() => window.REACT_APP_NAVIGATE('/')}
            className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Volver al Inicio
          </button>
        </div>
      </div>
    );
  }

  const upcomingEvents = events.filter(event => new Date(event.event_date) > new Date());
  const pastEvents = events.filter(event => new Date(event.event_date) <= new Date());

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            <button 
              onClick={() => window.REACT_APP_NAVIGATE(`/shelter/${id}`)}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-xl"></i>
              <span className="font-medium">Volver al Refugio</span>
            </button>
            
            <div className="flex items-center space-x-3">
              <img 
                src="/images/image.png" 
                alt="Huellitas Unidas Logo" 
                className="w-8 h-8 object-contain"
              />
              <h1 className="text-lg font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>
                Huellitas Unidas
              </h1>
            </div>

            <div className="flex items-center space-x-4">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="p-2 rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
              >
                <i className="ri-home-line text-xl text-gray-600"></i>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Title Section */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center">
              <i className="ri-calendar-event-fill text-white text-2xl"></i>
            </div>
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-gray-900">
                Eventos de {shelter.name}
              </h1>
              <p className="text-gray-600 mt-2">Únete a nuestras actividades y ayuda a los perritos</p>
            </div>
          </div>
        </div>

        {/* Upcoming Events */}
        {upcomingEvents.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center space-x-3 mb-8">
              <i className="ri-calendar-check-fill text-2xl text-green-500"></i>
              <h2 className="text-2xl font-bold text-gray-900">Próximos Eventos</h2>
              <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">
                {upcomingEvents.length} evento{upcomingEvents.length !== 1 ? 's' : ''}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {upcomingEvents.map((event) => {
                const images = [event.image_url_1, event.image_url_2, event.image_url_3, event.image_url_4, event.image_url_5].filter(Boolean);
                const eventDate = new Date(event.event_date);
                const isToday = eventDate.toDateString() === new Date().toDateString();
                const isTomorrow = eventDate.toDateString() === new Date(Date.now() + 86400000).toDateString();

                return (
                  <div key={event.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100">
                    <div className="relative">
                      <img
                        src={images[0] || 'https://readdy.ai/api/search-image?query=Animal%20shelter%20community%20event%20with%20volunteers%20and%20rescued%20dogs%2C%20outdoor%20gathering%20with%20people%20helping%20animals%2C%20warm%20community%20atmosphere%20with%20banners%20and%20activities%20for%20pet%20adoption%20awareness&width=400&height=300&seq=event-default&orientation=landscape'}
                        alt={event.title}
                        className="w-full h-48 object-cover object-top"
                      />
                      {images.length > 1 && (
                        <div className="absolute top-3 right-3 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          <i className="ri-image-line mr-1"></i>
                          {images.length} fotos
                        </div>
                      )}
                      <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-sm font-bold ${
                        isToday ? 'bg-red-500 text-white' : 
                        isTomorrow ? 'bg-orange-500 text-white' : 
                        'bg-green-500 text-white'
                      }`}>
                        {isToday ? '¡HOY!' : isTomorrow ? 'MAÑANA' : 'PRÓXIMO'}
                      </div>
                    </div>

                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-3">{event.description}</p>

                      <div className="space-y-3 mb-6">
                        <div className="flex items-center text-sm text-gray-700">
                          <i className="ri-calendar-line mr-3 text-purple-500 text-lg"></i>
                          <div>
                            <div className="font-semibold">
                              {eventDate.toLocaleDateString('es-ES', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </div>
                            <div className="text-gray-500">
                              {eventDate.toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-700">
                          <i className="ri-map-pin-line mr-3 text-red-500 text-lg"></i>
                          <div>
                            <div className="font-semibold">{event.location}</div>
                            <div className="text-gray-500 line-clamp-1">{event.address}</div>
                          </div>
                        </div>

                        <div className="flex items-center text-sm text-gray-700">
                          <i className="ri-group-line mr-3 text-blue-500 text-lg"></i>
                          <span className="font-semibold text-blue-600">
                            {event.attendees_count} persona{event.attendees_count !== 1 ? 's' : ''} confirmada{event.attendees_count !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => window.REACT_APP_NAVIGATE(`/event/${event.id}`)}
                        className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-eye-line mr-2"></i>
                        Ver más detalles
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Past Events */}
        {pastEvents.length > 0 && (
          <section className="mb-16">
            <div className="flex items-center space-x-3 mb-8">
              <i className="ri-calendar-check-fill text-2xl text-gray-400"></i>
              <h2 className="text-2xl font-bold text-gray-900">Eventos Pasados</h2>
              <div className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-sm font-semibold">
                {pastEvents.length} evento{pastEvents.length !== 1 ? 's' : ''}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {pastEvents.map((event) => {
                const images = [event.image_url_1, event.image_url_2, event.image_url_3, event.image_url_4, event.image_url_5].filter(Boolean);
                const eventDate = new Date(event.event_date);

                return (
                  <div key={event.id} className="bg-white rounded-2xl shadow-md overflow-hidden border border-gray-100 opacity-75">
                    <div className="relative">
                      <img
                        src={images[0] || 'https://readdy.ai/api/search-image?query=Animal%20shelter%20community%20event%20with%20volunteers%20and%20rescued%20dogs%2C%20outdoor%20gathering%20with%20people%20helping%20animals%2C%20warm%20community%20atmosphere%20with%20banners%20and%20activities%20for%20pet%20adoption%20awareness&width=400&height=300&seq=event-default&orientation=landscape'}
                        alt={event.title}
                        className="w-full h-48 object-cover object-top"
                      />
                      {images.length > 1 && (
                        <div className="absolute top-3 right-3 bg-black bg-opacity-60 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          <i className="ri-image-line mr-1"></i>
                          {images.length} fotos
                        </div>
                      )}
                      <div className="absolute top-3 left-3 bg-gray-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        FINALIZADO
                      </div>
                    </div>

                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-700 mb-3">{event.title}</h3>
                      <p className="text-gray-500 text-sm mb-4 line-clamp-3">{event.description}</p>

                      <div className="space-y-3 mb-6">
                        <div className="flex items-center text-sm text-gray-500">
                          <i className="ri-calendar-line mr-3 text-gray-400 text-lg"></i>
                          <div>
                            <div className="font-semibold">
                              {eventDate.toLocaleDateString('es-ES', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </div>
                            <div className="text-gray-400">
                              {eventDate.toLocaleTimeString('es-ES', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center text-sm text-gray-500">
                          <i className="ri-map-pin-line mr-3 text-gray-400 text-lg"></i>
                          <div>
                            <div className="font-semibold">{event.location}</div>
                            <div className="text-gray-400 line-clamp-1">{event.address}</div>
                          </div>
                        </div>

                        <div className="flex items-center text-sm text-gray-500">
                          <i className="ri-group-line mr-3 text-gray-400 text-lg"></i>
                          <span className="font-semibold">
                            {event.attendees_count} persona{event.attendees_count !== 1 ? 's' : ''} asistió{event.attendees_count !== 1 ? '' : ''}
                          </span>
                        </div>
                      </div>

                      <button
                        onClick={() => window.REACT_APP_NAVIGATE(`/event/${event.id}`)}
                        className="w-full bg-gray-100 hover:bg-gray-200 text-gray-600 py-3 px-4 rounded-xl font-bold transition-all duration-300 whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-eye-line mr-2"></i>
                        Ver detalles
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* No Events Message */}
        {events.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-calendar-event-line text-4xl text-gray-400"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              Eventos no programados por el momento
            </h3>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Este refugio aún no ha programado eventos públicos. Te invitamos a seguir sus redes sociales para estar al tanto de futuras actividades.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => window.REACT_APP_NAVIGATE(`/shelter/${id}`)}
                className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-arrow-left-line mr-2"></i>
                Volver al Refugio
              </button>
              <button 
                onClick={() => window.REACT_APP_NAVIGATE('/')}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-line mr-2"></i>
                Ver Adopciones
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de detalles del evento */}
      {showEventModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-3 h-full max-h-[90vh]">
              {/* Galería de imágenes - Columna izquierda expandida */}
              <div className="lg:col-span-2 relative bg-black flex flex-col">
                {/* Header del Modal - Solo en móvil */}
                <div className="lg:hidden sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                  <h3 className="text-xl font-bold text-gray-900">
                    Detalles del Evento
                  </h3>
                  <button
                    onClick={closeModals}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl text-gray-600"></i>
                  </button>
                </div>

                {/* Imagen principal - Ajustada para mostrar imagen completa */}
                <div className="flex-1 flex items-center justify-center p-4">
                  <img
                    src={displayEventImages[currentEventImageIndex]}
                    alt={`${selectedEvent.title} - Imagen ${currentEventImageIndex + 1}`}
                    className="max-w-full max-h-full object-contain"
                    style={{ maxHeight: 'calc(90vh - 120px)' }}
                  />
                </div>
                
                {/* Controles de navegación */}
                {displayEventImages.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentEventImageIndex(currentEventImageIndex > 0 ? currentEventImageIndex - 1 : displayEventImages.length - 1)}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 backdrop-blur-sm text-white p-3 rounded-full transition-all cursor-pointer"
                    >
                      <i className="ri-arrow-left-line text-xl"></i>
                    </button>
                    <button
                      onClick={() => setCurrentEventImageIndex(currentEventImageIndex < displayEventImages.length - 1 ? currentEventImageIndex + 1 : 0)}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white bg-opacity-20 hover:bg-opacity-40 backdrop-blur-sm text-white p-3 rounded-full transition-all cursor-pointer"
                    >
                      <i className="ri-arrow-right-line text-xl"></i>
                    </button>
                  </>
                )}

                {/* Contador e indicadores */}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center space-y-3">
                  {/* Contador de imágenes */}
                  <div className="bg-black bg-opacity-60 text-white px-4 py-2 rounded-full text-sm">
                    {currentEventImageIndex + 1} / {displayEventImages.length}
                  </div>
                  
                  {/* Indicadores de punto */}
                  {displayEventImages.length > 1 && displayEventImages.length <= 10 && (
                    <div className="flex space-x-2">
                      {displayEventImages.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentEventImageIndex(index)}
                          className={`w-3 h-3 rounded-full transition-all cursor-pointer ${
                            currentEventImageIndex === index 
                              ? 'bg-white' 
                              : 'bg-white bg-opacity-40 hover:bg-opacity-60'
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Detalle del evento (derecha) */}
              <div className="lg:col-span-1 bg-white flex flex-col">
                {/* Header del Modal - Solo en desktop */}
                <div className="hidden lg:block sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between z-10">
                  <h3 className="text-xl font-bold text-gray-900">
                    Detalles del Evento
                  </h3>
                  <button
                    onClick={closeModals}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-xl text-gray-600"></i>
                  </button>
                </div>

                {/* Contenido scrolleable */}
                <div className="flex-1 p-6 overflow-y-auto">
                  <h1 className="text-2xl font-bold text-gray-900 mb-4">
                    {selectedEvent.title}
                  </h1>

                  <div className="prose prose-lg max-w-none text-gray-700 mb-8">
                    <p className="leading-relaxed">{selectedEvent.description}</p>
                  </div>

                  {/* Información del Refugio */}
                  <div className="bg-purple-50 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-semibold text-purple-800 mb-3 flex items-center">
                      <i className="ri-building-fill mr-2"></i>
                      Organizado por
                    </h3>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                        <i className="ri-building-fill text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-purple-900">{shelter?.name}</h4>
                        <p className="text-purple-700 text-sm">{shelter?.location}</p>
                      </div>
                    </div>
                  </div>

                  {/* Sidebar de Información */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    {/* Fecha y Hora */}
                    <div className="mb-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                          <i className="ri-calendar-event-fill text-purple-500 text-xl"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Fecha y Hora</h4>
                          <p className="text-sm text-gray-600">Información del evento</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="font-semibold text-gray-900">
                          {new Date(selectedEvent.event_date).toLocaleDateString('es-ES', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                        <div className="text-purple-600 font-medium">
                          {new Date(selectedEvent.event_date).toLocaleTimeString('es-ES', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Ubicación */}
                    <div className="mb-6">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                          <i className="ri-map-pin-fill text-red-500 text-xl"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Ubicación</h4>
                          <p className="text-sm text-gray-600">Lugar del evento</p>
                        </div>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        <div className="font-semibold text-gray-900">
                          {selectedEvent.location}
                        </div>
                        <div className="text-gray-600">
                          {selectedEvent.address}
                        </div>
                      </div>
                    </div>

                    {/* Asistentes */}
                    <div className="mb-8">
                      <div className="flex items-center space-x-3 mb-4">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                          <i className="ri-group-fill text-blue-500 text-xl"></i>
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">Asistentes</h4>
                          <p className="text-sm text-gray-600">Confirmados hasta ahora</p>
                        </div>
                      </div>
                      
                      <div className="text-center">
                        <div className="text-3xl font-bold text-blue-600 mb-1">
                          {selectedEvent.attendees_count}
                        </div>
                        <div className="text-sm text-gray-600">
                          persona{selectedEvent.attendees_count !== 1 ? 's' : ''} confirmada{selectedEvent.attendees_count !== 1 ? 's' : ''}
                        </div>
                      </div>
                    </div>

                    {/* Botón de Asistencia */}
                    {new Date(selectedEvent.event_date) > new Date() ? (
                      <button
                        onClick={handleAttendEvent}
                        className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-4 px-6 rounded-xl font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-hand-heart-fill mr-2"></i>
                        ¡Asistiré!
                      </button>
                    ) : (
                      <div className="w-full bg-gray-100 text-gray-600 py-4 px-6 rounded-xl font-bold text-lg text-center">
                        <i className="ri-check-double-line mr-2"></i>
                        Evento Finalizado
                      </div>
                    )}
                  </div>
                </div>

                {/* Botón cerrar fijo en desktop */}
                <div className="hidden lg:block absolute top-4 right-4">
                  <button
                    onClick={closeModals}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors cursor-pointer bg-white shadow-lg"
                  >
                    <i className="ri-close-line text-xl text-gray-600"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Asistencia */}
      {showAttendModal && selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-event-fill text-white text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Confirmar Asistencia
              </h3>
              <p className="text-gray-600">
                {selectedEvent.title}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {new Date(selectedEvent.event_date).toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>

            <form onSubmit={submitAttendance}>
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre completo *
                  </label>
                  <input
                    type="text"
                    required
                    value={attendeeForm.name}
                    onChange={(e) => setAttendeeForm(prev => ({ ...prev, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Tu nombre completo"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Teléfono (opcional)
                  </label>
                  <input
                    type="tel"
                    value={attendeeForm.phone}
                    onChange={(e) => setAttendeeForm(prev => ({ ...prev, phone: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Tu número de teléfono"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email (opcional)
                  </label>
                  <input
                    type="email"
                    value={attendeeForm.email}
                    onChange={(e) => setAttendeeForm(prev => ({ ...prev, email: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="tu@email.com"
                  />
                </div>
              </div>

              <div className="bg-purple-50 rounded-xl p-4 mb-6">
                <div className="flex items-start space-x-3">
                  <i className="ri-information-line text-purple-500 text-lg mt-0.5"></i>
                  <div className="text-sm text-purple-700">
                    <p className="font-semibold mb-1">Información importante:</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Tu asistencia será confirmada inmediatamente</li>
                      <li>• El refugio podrá contactarte si es necesario</li>
                      <li>• Ayuda a los perritos participando en el evento</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={closeModals}
                  className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-800 py-3 px-4 rounded-xl font-semibold transition-all duration-300 cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white py-3 px-4 rounded-xl font-semibold transition-all duration-300 disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Confirmando...
                    </>
                  ) : (
                    <>
                      <i className="ri-check-line mr-2"></i>
                      Confirmar
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
